import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import { LEVELS, answerText, regionForLevel } from "./utils.js"
import { track } from "./analytics.js"
import IconCopy from "../images/icon-copy.svg?react"
import IconCheck from "../images/icon-check.svg?react"
import IconPlus from "../images/icon-plus.svg?react"

function MathMarkdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
      {children}
    </ReactMarkdown>
  )
}

function LoadingDots() {
  return (
    <div className="dots">
      <span />
      <span />
      <span />
    </div>
  )
}

export default function SolvePage({ problem, steps, answer, status, level, onNewQuestion }) {
  const region = regionForLevel(level)
  const endRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const isStreaming = status === "PENDING" || status === "STREAMING"

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    })
    return () => cancelAnimationFrame(id)
  }, [steps.length, answer])

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1800)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = () => {
    const text = answer?.answer?.symbolic || answerText(answer)
    if (!text) return
    navigator.clipboard?.writeText(text)
    track("copy", { level, region: regionForLevel(level) })
    setCopied(true)
  }

  return (
    <div className="solve-page">
      <div className="solve-content">
        <div className="question-bubble">{problem}</div>

        <div className="answer-card">
          <div className="steps-label">Step-by-step solution</div>
          <div className="steps-area">
            {steps.map((step) => (
              <div key={step.index} className="step-card">
                <div className="step-num">Step {step.index + 1}</div>
                {__DEBUG__ && <div className="step-title">{step.call}</div>}
                {step.explanation && step.explanation.trim() ? (
                  <div className="step-body">
                    <MathMarkdown>{step.explanation}</MathMarkdown>
                  </div>
                ) : isStreaming ? (
                  <LoadingDots />
                ) : (
                  <div className="step-body step-body--empty">No explanation provided.</div>
                )}
              </div>
            ))}

            {isStreaming && steps.length === 0 && (
              <div className="step-card step-card--loading">
                <LoadingDots />
              </div>
            )}
          </div>

          {answer && (
            <div className="answer-top">
              <div className="answer-label">Answer{__DEBUG__ && answer.verified ? " ✓" : ""}</div>
              <div className="answer-value">
                <MathMarkdown>{`$$${answerText(answer) || "?"}$$`}</MathMarkdown>
              </div>
              <div className="answer-tags">
                {level && <span className="tag neutral">{LEVELS[region]?.[level]?.label || level}</span>}
                {region && <span className="tag neutral">{region}</span>}
              </div>
              <button
                type="button"
                className={`act-btn ${copied ? "act-btn--copied" : ""}`}
                onClick={handleCopy}
                disabled={copied}
              >
                {copied ? <IconCheck width={14} height={14} /> : <IconCopy width={14} height={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          <div ref={endRef} />
        </div>

        {answer && (
          <p className="ai-disclaimer">
            Solutions are generated by AI and may contain mistakes. Please verify the result before relying on it.
          </p>
        )}
      </div>

      <div className="floating-footer">
        <button type="button" className="new-q-btn" onClick={onNewQuestion}>
          New question
          <IconPlus width={20} height={20} />
        </button>
      </div>
    </div>
  )
}
